import { gsap, ScrollTrigger } from 'gsap/all'
import { isMdScreen } from 'src/helpers/layout'

// Global flag to track if the initial site load/refresh has occurred
let initialLoadComplete = false;

/**
 * Checks if this is the initial site load or a refresh.
 * Returns true only for the first time it's called in a browser session.
 */
export const isInitialLoad = () => {
  if (typeof window === 'undefined') return true;
  return !initialLoadComplete;
};

/**
 * Marks the initial load as complete. Should be called after the first mount of global layout.
 */
export const markInitialLoadComplete = () => {
  initialLoadComplete = true;
};

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

const SINGLE_ANIMATION_TIME_DESKTOP = 0.65
const SINGLE_ANIMATION_TIME_MOBILE = 0.45
const TIME_BETWEEN_ANIMATIONS_TRIGGERS_DESKTOP = 0.12
const TIME_BETWEEN_ANIMATIONS_TRIGGERS_MOBILE = 0.10

const animationsOverlapDesktop =
  SINGLE_ANIMATION_TIME_DESKTOP - TIME_BETWEEN_ANIMATIONS_TRIGGERS_DESKTOP

const animationsOverlapMobile =
  SINGLE_ANIMATION_TIME_MOBILE - TIME_BETWEEN_ANIMATIONS_TRIGGERS_MOBILE

export const getAnimationTriggerPoint = () => {
  if (isMdScreen()) {
    return 'top 78%'
  }

  return 'top 82%'
}

export const getAnimationStepInSeconds = (timeline) => {
  const timeLeftOfCurrentAnimation = timeline.duration() - timeline.time()

  if (isMdScreen() && timeLeftOfCurrentAnimation > animationsOverlapDesktop) {
    return `-=${animationsOverlapDesktop}`
  }

  if (!isMdScreen() && timeLeftOfCurrentAnimation > animationsOverlapMobile) {
    return `-=${animationsOverlapMobile}`
  }

  return null
}

export const getAnimationDurationInSeconds = () => {
  if (isMdScreen()) {
    return SINGLE_ANIMATION_TIME_DESKTOP
  }

  return SINGLE_ANIMATION_TIME_MOBILE
}

const ANIMATION_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

export const createShowFromBottomAnimation = (
  timeline,
  element: Element,
  triggerElement?: Element,
  position?: string | number,
  startPoint?: string
) => {
  if (!element) return

  gsap.set(element, {
    y: 35,
    opacity: 0,
    filter: 'blur(10px)',
    willChange: 'transform, opacity, filter',
  })

  ScrollTrigger.create({
    trigger: triggerElement || element,
    start: startPoint || getAnimationTriggerPoint(),
    onEnter: () => {
      timeline.to(
        element,
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: getAnimationDurationInSeconds(),
          ease: ANIMATION_EASE,
          clearProps: 'filter,willChange,transform',
        },
        position !== undefined ? position : getAnimationStepInSeconds(timeline)
      )
    },
  })

  ScrollTrigger.sort()
}

export const createShowFromLeftAnimation = (
  timeline,
  element: Element,
  triggerElement?: Element,
  position?: string | number
) => {
  if (!element) return

  gsap.set(element, {
    x: -40,
    opacity: 0,
    filter: 'blur(10px)',
    willChange: 'transform, opacity, filter',
  })

  ScrollTrigger.create({
    trigger: triggerElement || element,
    start: getAnimationTriggerPoint(),
    onEnter: () => {
      timeline.to(
        element,
        {
          x: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: getAnimationDurationInSeconds(),
          ease: ANIMATION_EASE,
          clearProps: 'filter,willChange',
        },
        position !== undefined ? position : getAnimationStepInSeconds(timeline)
      )
    },
  })
}

export const createShowFromRightAnimation = (
  timeline,
  element: Element,
  triggerElement?: Element,
  position?: string | number
) => {
  if (!element) return

  gsap.set(element, {
    x: 40,
    opacity: 0,
    filter: 'blur(10px)',
    willChange: 'transform, opacity, filter',
  })

  ScrollTrigger.create({
    trigger: triggerElement || element,
    start: getAnimationTriggerPoint(),
    onEnter: () => {
      timeline.to(
        element,
        {
          x: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: getAnimationDurationInSeconds(),
          ease: ANIMATION_EASE,
          clearProps: 'filter,willChange',
        },
        position !== undefined ? position : getAnimationStepInSeconds(timeline)
      )
    },
  })
}

export default {}
